import MobileAppBar from "../MobileAppBar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import BottomNavigation from "../BottomNavigation";
import { useNavigationStore } from "presentation/mobile/stores/navigation-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import Spinner from "presentation/components/Shared/Spinner";
import { useUserByFilter } from "presentation/hooks/use-query-user";
import { UserInputFilter } from "domain/models/user-input-filter";
import { usePermissionByFilter } from "presentation/hooks/use-query-permission";
import { PermissionInputFilter } from "domain/models/permission-input-filter";
import { Permission } from "domain/entities/permission";
import { useImpersonationStore } from "presentation/stores/use-impersonation-store";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import { clearLocalStorage } from "presentation/utils/localStorage";
import { useFunctions } from "presentation/hooks/use-query-functions";
import { getMobileRoutes } from "presentation/mobile/utils/mobile-routes.util";
import { useRolesStore } from "presentation/stores/use-roles-list-store";

const MobileLayout = () => {
    const { currentRoute } = useNavigationStore();

    //  Adicionar accessToken e hasHydrated
    const {
        email,
        accessToken,
        hasHydrated,
        userEmployee,
        setUserPermissions,
        setUserEmployee,
        clearAuth,
    } = useAuthStore();

    const navigationStore = useNavigationStore();
    const navigate = useNavigate();
    const { isDebugMode } = useImpersonationStore();
    const { setRoles } = useRolesStore();

    //  Logs de diagnóstico
    //console.log(" [MobileLayout] hasHydrated:", hasHydrated);
    //console.log(" [MobileLayout] accessToken:", accessToken ? "presente" : "ausente");
    //console.log(" [MobileLayout] email:", email);

    // Só faz as queries quando o store está hidratado E tem token
    const shouldFetch = hasHydrated && !!accessToken && !!email;

    const rolesEmail = isDebugMode && userEmployee?.emailAddress ? userEmployee.emailAddress : email;

    const { data: userData, isLoading: isUserLoading, error } = useUserByFilter(
        shouldFetch ? { email: rolesEmail } as UserInputFilter : { email: "__disabled__" } as UserInputFilter
    );

    const { data: functions, isLoading: isFunctionLoading } = useFunctions(
        shouldFetch
            ? { limit: 100, sortBy: "order", sortOrder: "asc" }
            : { limit: 0 }
    );

    const { data: employeeData, refetch: employeeRefetch } = useEmployeesByFilter(
        shouldFetch ? { email: email } as EmployeeInputFilter : { email: "__disabled__" } as EmployeeInputFilter
    );

    const { data: permissionsData, isLoading: isPermissionsLoading, refetch: permissionsRefetch } =
        usePermissionByFilter(
            shouldFetch ? { limit: 100 } as PermissionInputFilter : { limit: 0 } as PermissionInputFilter
        );

    // Só verifica autenticação depois de hidratar
    useEffect(() => {
        if (!hasHydrated) {
            // console.log("[MobileLayout] A aguardar hidratação do store...");
            return;
        }

        if (!accessToken) {
            //  console.log("[MobileLayout] Sem token após hidratação, redirecionando para login");
            navigate("/login");
        }
    }, [hasHydrated, accessToken, navigate]);

    useEffect(() => {
        if (userData?.data && !isDebugMode) {
            setRoles(userData.data[0].roles);
        }
    }, [userData, isDebugMode]);

    const setRoutes = useNavigationStore((state) => state.setRoutes);

    // Recalcula sempre que o utilizador/permissões mudam
    useEffect(() => {
        if (!shouldFetch) return;
        const currentUser = userData?.data?.[0];
        if (
            !functions?.data ||
            !currentUser ||
            !permissionsData ||
            isUserLoading ||
            isFunctionLoading ||
            isPermissionsLoading
        )
            return;

        let permissions: Permission[] = [];
        currentUser.roles.forEach((item) => {
            const selectedPermission = permissionsData?.data?.filter(
                (subitem) => subitem.roleId === item.id
            );
            permissions = [...permissions, ...(selectedPermission || [])];
        });

        const mobileRoutes = getMobileRoutes(
            functions.data,
            permissions,
            currentUser.roles
        );
        setRoutes(mobileRoutes);
    }, [
        functions?.data,
        userData?.data,
        permissionsData,
        isFunctionLoading,
        isPermissionsLoading,
        isUserLoading,
        shouldFetch,
        setRoutes,
    ]);


    // Proteção contra acesso direto por URL a módulos não permitidos
    const location = useLocation();
    const currentSegment = location.pathname.split("/")[2] ?? "";

    const isCurrentPathAllowed = useMemo(() => {
        if (!currentSegment) return true; // Home
        if (navigationStore.routes.length === 0) return false; // rotas ainda a calcular
        const target = `/mobile/${currentSegment}`;
        return navigationStore.routes.some(
            (r) => r.route === target || r.subRoutes?.some((s) => s.route === target)
        );
    }, [currentSegment, navigationStore.routes]);

    useEffect(() => {
        if (navigationStore.routes.length === 0) return;
        if (!isCurrentPathAllowed) navigate("/mobile", { replace: true });
    }, [isCurrentPathAllowed, navigationStore.routes.length, navigate]);


    useEffect(() => {
        if (!shouldFetch) return;
        if (isUserLoading) return;
        if (error || !userData?.data) {
            // Só fazer logout se realmente não há token no sessionStorage
            const raw = sessionStorage.getItem("auth-session-storage");
            let storedToken = null;
            if (raw) {
                try {
                    const parsed = JSON.parse(raw);
                    storedToken = parsed?.state?.accessToken;
                } catch {
                    // ignora
                }
            }

            if (storedToken) {
                // console.log("[MobileLayout] Erro na query mas sessionStorage tem token. A aguardar...");
                return;
            }

            // console.log("[MobileLayout] Erro e sem token no sessionStorage, a fazer logout");
            clearAuth();
            clearLocalStorage();
            navigate("/login?unauthorized=true");
        }
    }, [userData, isUserLoading, error, shouldFetch, clearAuth, navigate]);

    useEffect(() => {
        if (!shouldFetch) return;
        if (!isDebugMode && userData?.data?.[0]?.roles.length) {
            let permissions: Permission[] = [];
            userData?.data[0].roles.map((item) => {
                const selectedPermission = permissionsData?.data?.filter(
                    (subitem) => subitem.roleId === item.id
                );
                permissions = [...permissions, ...(selectedPermission || [])];
            });
            setUserPermissions(permissions);
        }
    }, [userData, permissionsData, isDebugMode, shouldFetch, setUserPermissions]);

    useEffect(() => {
        if (!shouldFetch) return;
        if (employeeData?.data && !isDebugMode) {
            setUserEmployee(employeeData.data[0]);
            if (employeeData.data[0].isActive === false) {
                clearAuth();
                clearLocalStorage();
                navigate("/login?unauthorized=true");
            }
        }
    }, [employeeData, isDebugMode, shouldFetch, setUserEmployee, clearAuth, navigate]);

    // Spinner enquanto hidrata ou carrega
    if (!hasHydrated || !accessToken || isUserLoading) {
        return <Spinner />;
    }

    return (
        <div className="flex flex-column py-0" style={{ height: "100dvh", minHeight: "100dvh" }}>
            <MobileAppBar />
            <div className="flex-grow-1 overflow-auto " style={{ backgroundColor: "#fbfbfb" }}>
                {isCurrentPathAllowed ? <Outlet /> : <Spinner />}
            </div>
            {currentRoute !== "home" && <BottomNavigation />}
        </div>
    );
};

export default MobileLayout;